import { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  clearPaymentMethodsData,
  paymentMethods,
  savePaymentMethodData,
} from '../../slices/payments';

import store from '../../store';
import { CartInStore } from '../../slices/cart/types';
import { placeOrder } from '../../slices/orders';
import {
  ShopPayDeliveryMethodChangedEvent,
  ShopPayPaymentAttemptFailedEvent,
  ShopPayPaymentCompleteEvent,
  ShopPayPaymentConfirmationRequestedEvent,
  ShopPayPaymentRequest,
  ShopPayPaymentRequestSession,
  ShopPayShippingAddressChangedEvent,
} from './types';
import { calculateTotalGiftCards } from '../../utils';
import { useShopPayContext } from '../../contexts/ShopPayContext';
import ordersMasterService from '../../services/orders/ordersMasterService';

// TODO: Address validation disabled in order to improve modal performance
// import cartService from '../../services/cart';

import {
  setShippingAddressSteppedCheckout,
  setBillingAddress,
  setCart,
} from '../../slices/cart';

import {
  getAvailableShippingMethods,
  setShippingMethodsForAllProductsInCart,
} from '../../slices/shippingMethods';
import {
  GetShippingMethodsResponse,
  SetShippingMethodsEntry,
} from '../../slices/shippingMethods/types';
import shopPayService from '../../services/shopPay';
import paymentService from '../../services/paymentJS';
import { NON_CONTINENTAL_STATES } from '../../blocks/pdp/constants';
import { IN_HOME_SETUP_FREE_SHIPPING_METHOD_KEY } from '../../utils/common/constants';

declare global {
  interface Window {
    ShopPay?: {
      PaymentRequest: {
        configure: (config: Record<string, unknown>) => void;
        createButton: () => { render: (selector: string) => void };
        createLogin: (config: Record<string, unknown>) => {
          render: (selector: string) => void;
        };
        build: (param: ShopPayPaymentRequest) => ShopPayPaymentRequest;
        createSession: (param: {
          paymentRequest: ShopPayPaymentRequest;
        }) => ShopPayPaymentRequestSession;
      };
    };
  }
}

const useShopPaySession = () => {
  const dispatch = useDispatch();
  /*   const { formatMessage } = useIntl(); */
  // const [isShopPayLoaded, setIsShopPayLoaded] = useState<boolean>(false);

  const { setIsShopPaySessionCreated } = useShopPayContext();

  const { locale } = useIntl();

  const shopPaySessionRef = useRef<ShopPayPaymentRequestSession>();

  const {
    lineItems,
    giftCards,
    taxedPrice,
    estimatedTaxAmount,
    id: cartId,
    isDraft,
  } = useSelector((state: { cart: CartInStore }) => state.cart);

  const totalFromStore = useSelector((state: { cart: CartInStore }) => {
    if (state.cart.taxedPrice) {
      return state.cart.taxedPrice.totalGross;
    }
    return state.cart.totalPrice;
  });

  const taxes = taxedPrice
    ? taxedPrice.taxPortions.reduce(
        (
          totalTaxAmount: number,
          taxPortion: { amount: { centAmount: number } },
        ) => totalTaxAmount + taxPortion.amount.centAmount,
        0,
      )
    : estimatedTaxAmount?.centAmount || 0;

  const total = {
    centAmount: Math.max(
      0,
      totalFromStore.centAmount - calculateTotalGiftCards({ giftCards }),
    ),
    currencyCode: totalFromStore.currencyCode,
    fractionDigits: totalFromStore.fractionDigits,
  };

  const SHOP_PAY_SHOP_ID = +(process.env.SHOP_PAY_SHOP_ID ?? '0');
  const SHOP_PAY_CLIENT_ID = process.env.SHOP_PAY_CLIENT_ID ?? '';

  const cartHasLineItems = lineItems?.length > 0;

  useEffect(() => {
    const onLoadShopPaySDKScript = () => {
      if (window.ShopPay && cartHasLineItems) {
        // eslint-disable-next-line no-console
        console.log('ShopPay loaded!');
        window.ShopPay.PaymentRequest.configure({
          shopId: SHOP_PAY_SHOP_ID,
          clientId: SHOP_PAY_CLIENT_ID,
          debug: true,
          onAnalyticsEvent: (event: { type: string }) => {
            switch (event.type) {
              case 'loginpromptdisplayed':
                // eslint-disable-next-line no-console
                console.log('shopPay Login prompt was displayed.');
                break;
              case 'windowopened':
                // eslint-disable-next-line no-console
                console.log('shopPay Window was opened.');
                break;
              case 'windowclosed':
                // eslint-disable-next-line no-console
                console.log('shopPay Window was closed.');
                break;
              default:
                // eslint-disable-next-line no-console
                console.log('shopPay Unknown event:', event);
            }
          },
        });

        const initialPaymentRequest = window.ShopPay.PaymentRequest.build({
          lineItems: [
            ...lineItems.map((lineItem) => ({
              label: lineItem.name['en-US'] ?? '',
              quantity: lineItem.quantity,
              sku: lineItem.variant.sku,
              requiresShipping: true,
              originalItemPrice: {
                amount:
                  lineItem.price.value.centAmount /
                  10 ** lineItem.price.value.fractionDigits,
                currencyCode: lineItem.price.value.currencyCode,
              },
              finalItemPrice: {
                amount:
                  lineItem.price.value.centAmount /
                  10 ** lineItem.price.value.fractionDigits,
                currencyCode: lineItem.price.value.currencyCode,
              },
              finalLinePrice: {
                amount:
                  lineItem.totalPrice.centAmount /
                  10 ** lineItem.totalPrice.fractionDigits,
                currencyCode: lineItem.totalPrice.currencyCode,
              },
              originalLinePrice: {
                amount:
                  lineItem.totalPrice.centAmount /
                  10 ** lineItem.totalPrice.fractionDigits,
                currencyCode: lineItem.totalPrice.currencyCode,
              },
              lineDiscounts: [],
            })),
          ],
          discountCodes: [],
          deliveryMethods: [
            {
              amount: {
                amount: 0,
                currencyCode: 'USD',
              },
              code: 'STANDARD',
              label: 'Standard shipping',
            },
          ],
          subtotal: {
            amount: (total.centAmount - taxes) / 10 ** total.fractionDigits,
            currencyCode: total.currencyCode,
          },
          totalTax: {
            amount: taxes / 10 ** total.fractionDigits,
            currencyCode: total.currencyCode,
          },
          total: {
            amount: total.centAmount / 10 ** total.fractionDigits,
            currencyCode: total.currencyCode,
          },
          presentmentCurrency: 'USD',
          locale: 'en',
          shippingLines: [
            {
              amount: { amount: 0, currencyCode: 'USD' },
              code: 'STANDARD',
              label: 'Standard Shipping',
            },
          ],
        });
        // eslint-disable-next-line no-console
        console.log('initialPaymentRequest', initialPaymentRequest);

        const { cart: initialCart } = store.getState();

        const shopPayPaymentRequestWithConverter =
          shopPayService.convertCartToShopPay(initialCart);

        // eslint-disable-next-line no-console
        console.log(
          'shopPayPaymentRequestWithConverter',
          shopPayPaymentRequestWithConverter,
        );

        const session = window.ShopPay.PaymentRequest.createSession({
          paymentRequest: shopPayPaymentRequestWithConverter,
        });

        setIsShopPaySessionCreated(true);

        shopPaySessionRef.current = session;

        session.addEventListener('sessionrequested', async (ev: unknown) => {
          // eslint-disable-next-line no-console
          console.log('shopPay >> sessionRequested ev', ev);

          const PROVIDER = 'SHOP_PAY';

          if (shopPaySessionRef.current) {
            const responseJson = (await ordersMasterService.callToMethod({
              cartId,
              isDraft,
              methodName: ordersMasterService.methodsMap.authorizeSession,
              paramsObject: { provider: PROVIDER },
            })) as {
              token: string;
              checkoutUrl: string;
              sourceIdentifier: string;
            };
            // eslint-disable-next-line no-console
            console.log('shopPay sessionRequested responseJson', responseJson);

            const { token, checkoutUrl, sourceIdentifier } = responseJson;
            // eslint-disable-next-line no-console
            console.log('shopPay sessionRequested token', token);
            // eslint-disable-next-line no-console
            console.log('shopPay sessionRequested checkoutUrl', checkoutUrl);
            // eslint-disable-next-line no-console
            console.log(
              'shopPay sessionRequested sourceIdentifier',
              sourceIdentifier,
            );

            const { cart: updatedCart } = store.getState();

            const shopPayPaymentUpdatedRequest =
              shopPayService.convertCartToShopPay(updatedCart);

            const updatedPaymentRequest =
              window.ShopPay?.PaymentRequest.build(
                shopPayPaymentUpdatedRequest,
              ) ?? undefined;

            shopPaySessionRef.current.completeSessionRequest({
              token,
              checkoutUrl,
              sourceIdentifier,
              updatedPaymentRequest,
            });
          }
        });

        session.addEventListener('deliverymethodtypechanged', () => {
          // eslint-disable-next-line no-console
          console.log('shopPay >> deliverymethodtypechanged');
          if (shopPaySessionRef.current) {
            const currentPaymentRequest =
              shopPaySessionRef.current.paymentRequest;

            // Update the payment request based on the delivery method type change
            const updatedPaymentRequest = window.ShopPay?.PaymentRequest.build({
              ...currentPaymentRequest,
            });

            shopPaySessionRef.current.completeDeliveryMethodTypeChange({
              updatedPaymentRequest,
            });
          }
        });

        session.addEventListener(
          'shippingaddresschanged',
          async (ev: ShopPayShippingAddressChangedEvent) => {
            // eslint-disable-next-line no-console
            console.log('shopPay >> shippingaddresschanged');
            if (shopPaySessionRef.current) {
              const currentPaymentRequest =
                shopPaySessionRef.current.paymentRequest;
              // eslint-disable-next-line no-console
              console.log('shopPay >> shippingaddresschanged ev', ev);
              const selectedShippingAddress = ev.shippingAddress;
              // eslint-disable-next-line no-console
              console.log(
                'shopPay >> shippingaddresschanged selectedShippingAddress',
                selectedShippingAddress,
              );

              if (
                NON_CONTINENTAL_STATES.includes(
                  selectedShippingAddress.provinceCode ?? '',
                ) ||
                selectedShippingAddress.countryCode !== 'US'
              ) {
                shopPaySessionRef.current.completeShippingAddressChange({
                  errors: [
                    {
                      type: 'shippingAddressError',
                      message:
                        // TODO: move this to i18n
                        'Shipping is not available for selected address.',
                    },
                  ],
                });
              } else {
                const shippingAddressDraftToAssign = {
                  streetAddress: selectedShippingAddress?.address1 ?? '',
                  city: selectedShippingAddress?.city ?? '',
                  postalCode: selectedShippingAddress?.postalCode ?? '',
                  country: selectedShippingAddress?.countryCode ?? '',
                  state: selectedShippingAddress?.provinceCode ?? '',
                  additionalStreetInfo: selectedShippingAddress?.address2 ?? '',
                  email: selectedShippingAddress?.email ?? '',
                  phone: selectedShippingAddress?.phone ?? '',
                  firstName: selectedShippingAddress?.firstName ?? '',
                  lastName: selectedShippingAddress?.lastName ?? '',
                };
                // eslint-disable-next-line no-console
                console.log(
                  'shopPay >> shippingaddresschanged shippingAddressDraftToAssign',
                  shippingAddressDraftToAssign,
                );

                // TODO: Address validation disabled to speed up modal loading
                /*
                const { cart } = store.getState();
                let validateAddressResponse;
                try {
                  // TODO: Create a type for this and remove conversion to unknown
                  validateAddressResponse = (await cartService.validateAddress({
                    ...shippingAddressDraftToAssign,
                    cartId: cart.id,
                  })) as { fedexChanges?: { text: string }[]; score: string };
                } catch (error) {
                  shopPaySessionRef.current.completeShippingAddressChange({
                    errors: [
                      {
                        type: 'shippingAddressError',
                        message:
                          // TODO: move this to i18n
                          'Shipping address not validated.',
                      },
                    ],
                  });
  
                  return;
                }
                
                // eslint-disable-next-line no-console
                console.log(
                  'shopPay >> shippingaddresschanged validateAddressResponse',
                  validateAddressResponse,
                ); */

                // eslint-disable-next-line @typescript-eslint/await-thenable
                await dispatch(
                  setShippingAddressSteppedCheckout({
                    shippingAddress: shippingAddressDraftToAssign,
                  }),
                );

                // eslint-disable-next-line @typescript-eslint/await-thenable
                dispatch(
                  setBillingAddress({
                    billingAddress: shippingAddressDraftToAssign,
                  }),
                );

                const { cart: cartWithUpdatedShippingAddress } =
                  store.getState();
                // eslint-disable-next-line no-console
                console.log(
                  'shopPay >> shippingaddresschanged cartWithUpdatedShippingAddres.shippingAddress',
                  cartWithUpdatedShippingAddress.shippingAddress,
                );

                // eslint-disable-next-line no-console
                console.log(
                  'shopPay >> shippingaddresschanged cartWithUpdatedShippingAddress',
                  cartWithUpdatedShippingAddress,
                );

                const shopPayPaymentUpdatedRequest =
                  shopPayService.convertCartToShopPay(
                    cartWithUpdatedShippingAddress,
                  );

                // TODO: Check when the address is valid enough to set it or return an error (there's no Address Validation modal to show here)
                // we need to decide between accepting the address from shopPay or not
                /*               if (
                  !validateAddressResponse?.fedexChanges?.filter(
                    (fedexChange) => fedexChange.text !== NO_CHANGES_VALUE,
                  ).length &&
                  validateAddressResponse.score === VALID_ADDRESS_SCORE
                ) {
                  onClickSave(shippingAddressDraftToAssign);
                } */
                // Update the payment request based on the shipping address change
                const updatedPaymentRequest =
                  window.ShopPay?.PaymentRequest.build({
                    ...currentPaymentRequest,
                    ...shopPayPaymentUpdatedRequest,
                    deliveryMethods: [
                      {
                        amount: { amount: 0, currencyCode: 'USD' },
                        code: 'STANDARD',
                        label: 'Standard shipping',
                        deliveryExpectationLabel: '7-10 days',
                      },
                    ],
                  });

                shopPaySessionRef.current.completeShippingAddressChange({
                  updatedPaymentRequest,
                });
              }
            }
          },
        );

        session.addEventListener(
          'deliverymethodchanged',
          async (ev: ShopPayDeliveryMethodChangedEvent) => {
            // eslint-disable-next-line no-console
            console.log('shopPay >> deliverymethodchanged');
            // eslint-disable-next-line no-console
            console.log('shopPay >> deliverymethodchanged ev', ev);
            // eslint-disable-next-line no-console
            console.log(
              'shopPay >> deliverymethodchanged running getAvailableShippingMethods',
            );
            // eslint-disable-next-line @typescript-eslint/await-thenable
            await dispatch(
              getAvailableShippingMethods({
                locale,
                overwritePaidInHomeSetupShipping: false,
                customInHomeSetupPricing: false,
              }),
            );

            const { shippingMethods } = store.getState() as {
              cart: CartInStore;
              shippingMethods: {
                availableShippingMethods: GetShippingMethodsResponse;
              };
            };

            const { availableShippingMethods } = shippingMethods;
            // eslint-disable-next-line no-console
            console.log(
              'shopPay >> deliverymethodchanged availableShippingMethods',
              availableShippingMethods,
            );

            const shippingMethodsToAssign: SetShippingMethodsEntry[] =
              availableShippingMethods.map((shippingMethod) => ({
                parentKey: shippingMethod.key,
                groups: shippingMethod.groups.map((group) => {
                  const isInHomeSetupFreeEligible =
                    !!group.shippingMethods.find(
                      (method) =>
                        method.key === IN_HOME_SETUP_FREE_SHIPPING_METHOD_KEY,
                    );

                  if (isInHomeSetupFreeEligible) {
                    return {
                      key: IN_HOME_SETUP_FREE_SHIPPING_METHOD_KEY,
                      name: 'In-Home Setup',
                      cost: 0,
                      needsRemoval: true,
                      skus: group.products.map((product) => product.sku),
                    };
                  }

                  return {
                    key: 'FEDEX_GROUND',
                    name: 'Standard Shipping',
                    cost: 0,
                    needsRemoval: false,
                    skus: group.products.map((product) => product.sku),
                  };
                }),
              }));

            // eslint-disable-next-line @typescript-eslint/await-thenable
            dispatch(
              setShippingMethodsForAllProductsInCart({
                shippingMethods: shippingMethodsToAssign,
                overwritePaidInHomeSetupShipping: false,
                customInHomeSetupPricing: false,
              }),
            );

            if (shopPaySessionRef.current) {
              const currentPaymentRequest =
                shopPaySessionRef.current.paymentRequest;
              const selectedDeliveryMethod = ev.deliveryMethod;

              let updatedRequestValues;

              if (selectedDeliveryMethod) {
                updatedRequestValues = {
                  shippingLines: [
                    {
                      label: selectedDeliveryMethod.label,
                      amount: selectedDeliveryMethod.amount,
                      code: selectedDeliveryMethod.code,
                    },
                  ],
                  totalShippingPrice: {
                    finalTotal: {
                      amount: selectedDeliveryMethod.amount.amount,
                      currencyCode: 'USD',
                    },
                  },
                  total: {
                    amount:
                      currentPaymentRequest.total.amount +
                      selectedDeliveryMethod.amount.amount,
                    currencyCode: 'USD',
                  },
                };
              } else {
                updatedRequestValues = {
                  total: {
                    amount: currentPaymentRequest.total.amount,
                    currencyCode: 'USD',
                  },
                };
              }

              // Update the payment request based on the delivery method change
              // and update the totals accordingly
              const updatedPaymentRequest =
                window.ShopPay?.PaymentRequest.build({
                  ...currentPaymentRequest,
                  ...updatedRequestValues,
                });

              shopPaySessionRef.current.completeDeliveryMethodChange({
                updatedPaymentRequest,
              });
            }
          },
        );

        session.addEventListener('pickuplocationchanged', () => {
          // eslint-disable-next-line no-console
          console.log('shopPay >> pickuplocationchanged');

          if (shopPaySessionRef.current) {
            const currentPaymentRequest =
              shopPaySessionRef.current?.paymentRequest;

            const updatedPaymentRequest = window.ShopPay?.PaymentRequest.build({
              ...currentPaymentRequest,
            });

            shopPaySessionRef.current.completePickupLocationChange({
              updatedPaymentRequest,
            });
          }
        });

        session.addEventListener('pickuplocationfilterchanged', () => {
          // eslint-disable-next-line no-console
          console.log('shopPay >> pickuplocationfilterchanged');

          if (shopPaySessionRef.current) {
            const currentPaymentRequest =
              shopPaySessionRef.current.paymentRequest;

            // Update the payment request based on the pickup location filter change
            // by filtering the available pickup locations based on a customer's location
            const updatedPaymentRequest = window.ShopPay?.PaymentRequest.build({
              ...currentPaymentRequest,
            });

            shopPaySessionRef.current.completePickupLocationFilterChange({
              updatedPaymentRequest,
            });
          }
        });

        session.addEventListener('discountcodechanged', () => {
          // eslint-disable-next-line no-console
          console.log('shopPay >> discountcodechanged');

          if (shopPaySessionRef.current) {
            shopPaySessionRef.current.completeDiscountCodeChange({
              errors: [
                {
                  type: 'discountCodeError',
                  message:
                    // TODO: move this to i18n
                    'Hmm, this code doesn’t match any current promotions. Try again.',
                },
              ],
            });
          }
        });

        session.addEventListener(
          'paymentconfirmationrequested',
          async (ev: ShopPayPaymentConfirmationRequestedEvent) => {
            // eslint-disable-next-line no-console
            console.log('shopPay >> paymentconfirmationrequested');

            if (shopPaySessionRef.current) {
              const shopPayPaymentMethodToken = ev?.paymentMethod ?? '';
              // Before submitting the payment request for processing, a final check should be done on your server
              // to make sure the payment request (total price, inventory available, etc.) is still valid.
              const { cart: updatedCart } = store.getState();

              const shopPayPaymentUpdatedRequest =
                shopPayService.convertCartToShopPay(updatedCart);
              try {
                await paymentService.createPaymentRequestSession({
                  token: shopPaySessionRef.current?.token ?? '',
                  shopPayPaymentMethodToken,
                  shopPayPaymentRequest: shopPayPaymentUpdatedRequest,
                });

                shopPaySessionRef.current.completePaymentConfirmationRequest();
              } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Error creating payment request session:', error);
                shopPaySessionRef.current.completePaymentConfirmationRequest({
                  errors: [
                    {
                      type: 'generalError',
                      message: 'Something went wrong. Please try again.',
                    },
                    // Optionally build an updated paymentRequest and include it here
                  ],
                });
              }
            }
          },
        );

        session.addEventListener(
          'paymentattemptfailed',
          (ev: ShopPayPaymentAttemptFailedEvent) => {
            // eslint-disable-next-line no-console
            console.log('shopPay >> paymentattemptfailed ev', ev);
          },
        );

        session.addEventListener(
          'paymentcomplete',
          async (ev: ShopPayPaymentCompleteEvent) => {
            // eslint-disable-next-line no-console
            console.log('shopPay >> paymentcomplete ev', ev);
            // eslint-disable-next-line no-console
            console.log('shopPay >> paymentcomplete');
            if (ev.processingStatus.status === 'completed') {
              dispatch(setCart({ status: 'loading' }));

              const { kountSessionId } = store.getState().payments as {
                kountSessionId: string;
              };
              localStorage.setItem('kountSessionId', kountSessionId ?? '');

              dispatch(clearPaymentMethodsData());
              dispatch(savePaymentMethodData({}));
              // eslint-disable-next-line @typescript-eslint/await-thenable
              await dispatch(
                placeOrder({
                  orderData: {
                    paymentMethod: paymentMethods.SHOP_PAY ?? '',
                    id: shopPaySessionRef.current?.token ?? '',
                    sessionId: kountSessionId ?? '',
                  },
                }),
              );

              const { order } = store.getState() as {
                order?: { id?: string; status: string };
              };

              if (order?.status === 'succeeded') {
                const orderId = order?.id ?? '';
                localStorage.setItem('orderId', orderId);
                session.close(); // close the Shop Pay popup
                window.location.href = '/order-confirmation';
              }
              dispatch(setCart({ status: 'succeeded' }));
            } else {
              // TODO: handle failure
            }
          },
        );

        session.addEventListener('windowclosed', () => {
          // eslint-disable-next-line no-console
          console.log('shopPay windowclosed!');
        });
      }
    };

    const SHOP_PAY_SCRIPT_URL =
      'https://cdn.shopify.com/shopifycloud/shop-js/shop-pay-payment-request.js';

    const loadShopPaySdk = () => {
      const script = document.createElement('script');
      script.src = SHOP_PAY_SCRIPT_URL;
      script.type = 'text/javascript';
      script.async = true;
      script.onload = onLoadShopPaySDKScript;
      document.getElementsByTagName('body')[0].appendChild(script);
    };

    if (!window.ShopPay && cartHasLineItems) {
      loadShopPaySdk();
    }
  }, [
    SHOP_PAY_CLIENT_ID,
    SHOP_PAY_SHOP_ID,
    cartHasLineItems,
    cartId,
    dispatch,
    isDraft,
    lineItems,
    locale,
    setIsShopPaySessionCreated,
    taxes,
    total.centAmount,
    total.currencyCode,
    total.fractionDigits,
  ]);

  return {};
};

export default useShopPaySession;
